/**
 * Shared service-worker update watcher (#4029), used by all three
 * `registerType: 'autoUpdate'` Vite PWAs (client-tech, time-client,
 * sales-client). Replaces three separate ad hoc implementations that each
 * dropped updates a different way — see the PR body for the full audit.
 *
 * WHY THIS FILE EXISTS INSTEAD OF LEANING ON workbox-window HARDER
 *
 * `vite-plugin-pwa`'s generated register.js is a thin wrapper around
 * workbox-window's `Workbox` class. Workbox's own `_onUpdateFound`
 * (workbox-window/Workbox.mjs) REMOVES ITS OWN `updatefound` listener from the
 * registration the first time it classifies an update as external:
 *
 *   if (updateLikelyTriggeredExternally) {
 *     this._externalSW = installingSW;
 *     registration.removeEventListener('updatefound', this._onUpdateFound);
 *   }
 *
 * So after the FIRST post-load deploy, workbox-window stops listening
 * entirely — a SECOND deploy's `updatefound` still fires natively on the
 * registration, but nothing inside workbox-window reacts to it, so
 * `onNeedReload` is never called again. Proven against the real library in
 * `__tests__/swUpdateWatcher.workbox.test.ts`.
 *
 * The fix is NOT to patch workbox-window (a dependency); it's to attach OUR
 * OWN listener directly to the `ServiceWorkerRegistration` — an ordinary
 * `EventTarget` that happily supports more than one `updatefound` listener,
 * completely unaffected by whatever workbox-window's own listener does to
 * itself.
 *
 * WHAT ELSE LIVES HERE
 *
 *  - The reload trigger (`controllerchange`) is deferred while a form-fill
 *    guard is held (#4741's formFillGuard), and flushed the moment the guard
 *    releases — never dropped, never immediate mid-fill.
 *  - `registration.update()` is throttled, but the throttle is stamped ONLY
 *    on a RESOLVED check. A rejected check (offline, captive portal) must not
 *    burn the window, or a technician stepping in and out of coverage can be
 *    locked out of ever finding a real update.
 *  - `visibilitychange` triggers a (throttled) check — a backgrounded PWA
 *    resumes without a browser-initiated update check of its own.
 *  - The last open form-fill guard releasing triggers an immediate
 *    (unthrottled) check, since the guard deliberately skipped checks the
 *    whole time it was held.
 */
import {
  isFormFillInProgress,
  shouldHoldUpdatesForFormFill,
  subscribeFormFill,
} from '../lib/forms/formFillGuard';
import { reloadAfterFlush } from './applyUpdate';
import { recordSwTakeoverWithoutReload } from './updateTelemetry';

/** Floor between `registration.update()` calls, stamped on success only. */
const THROTTLE_MS = 60_000;

let registration: ServiceWorkerRegistration | undefined;
let appName = 'unknown';
let lastSuccessfulCheckAt = 0;
let checkInFlight = false;
let reloadDeferred = false;
let updateFoundCount = 0;
/** Tracks `shouldHoldUpdatesForFormFill()` transitions -- CAP-BOUNDED, governs
 * only whether a fresh update check is forced back on (never a reload). */
let wasFillHeld = false;
/** Tracks `isFormFillInProgress()` transitions -- UNBOUNDED, governs whether a
 * deferred reload is safe to flush. See handleTakeover()'s header comment for
 * why these two predicates must never be conflated. */
let wasFillOpen = false;
let attached = false;
/** Whether a service worker ALREADY controlled the page before we started
 * watching. `controllerchange` fires on the very FIRST activation too (going
 * from no controller to one) -- that is a first install, not a takeover, and
 * must never reload (it would tear down the page mid-warm-up, exactly what
 * client-tech's own e2e fixture caught: reloading here raced the initial
 * `page.goto('/')` and destroyed its evaluate() execution context). */
let hadControllerAtAttach = false;

/**
 * Shared core for "the page just got (or is about to get) taken over by a new
 * service worker". `controllerchange` cannot be vetoed once it fires -- only
 * the RELOAD is deferrable: reloading immediately mid-fill would drop the
 * fill's in-flight photos/signature (see applyUpdate.ts); NOT reloading at
 * all would leave the page running old JS under a new controller
 * indefinitely, whose lazy chunks the old build can no longer fetch (Hosting
 * has already dropped them).
 *
 * Gates on `isFormFillInProgress()` -- UNBOUNDED -- not the capped
 * `shouldHoldUpdatesForFormFill()`. The FORM_FILL_MAX_HOLD_MS cap governs only
 * whether the app resumes LOOKING for updates (`shouldSkipForegroundUpdateCheck`);
 * it must never cause a reload to fire out from under a form that is still
 * open. A takeover arriving after the cap has expired, with the fill still
 * open, must defer exactly like one arriving before the cap.
 */
function handleTakeover(): void {
  if (isFormFillInProgress()) {
    reloadDeferred = true;
    recordSwTakeoverWithoutReload(appName);
    return;
  }
  reloadAfterFlush();
}

function onControllerChange(): void {
  if (!hadControllerAtAttach) {
    hadControllerAtAttach = true; // any FUTURE controllerchange is a real takeover
    return;
  }
  handleTakeover();
}

function onUpdateFound(): void {
  // Deliberately minimal: the value of this listener is existing at all,
  // attached directly to the registration (see file header) rather than
  // routed through workbox-window's internal, one-shot-for-external-updates
  // bookkeeping. The counter exists so the fix is verifiable in a unit test
  // without depending on workbox-window's own (buggy) event surface.
  updateFoundCount += 1;
}

function onVisibilityChange(): void {
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'visible') checkForSwUpdate(false);
}

/** `focus` / `online`: the same "the user is back, look for a new build"
 * moment as visibilitychange, on platforms/paths where visibilitychange alone
 * doesn't fire (window focus without a tab-visibility change; regaining a
 * network connection while already foregrounded). */
function onForegroundTrigger(): void {
  checkForSwUpdate(false);
}

function onFormFillChange(): void {
  const held = shouldHoldUpdatesForFormFill();
  const open = isFormFillInProgress();

  // The fill has GENUINELY closed (no open fill surfaces remain) -- and only
  // then is it safe to flush a reload deferred by handleTakeover(). The
  // cap-expiry transition below (held flips false while open stays true) must
  // never reach this branch: that is exactly the CRITICAL bug this guards
  // against (a reload wiping an in-progress fill at the 30-min mark).
  if (wasFillOpen && !open && reloadDeferred) {
    reloadDeferred = false;
    reloadAfterFlush();
  }

  // The capped hold expiring (independent of whether the fill is still open)
  // resumes update checks -- the guard deliberately skipped them for its
  // whole duration (up to FORM_FILL_MAX_HOLD_MS). This branch is reload-blind
  // by construction: it only ever calls checkForSwUpdate.
  if (wasFillHeld && !held) {
    checkForSwUpdate(true);
  }

  wasFillHeld = held;
  wasFillOpen = open;
}

let unsubscribeFormFillGuard: (() => void) | null = null;

function wireModuleLevelListeners(): void {
  wasFillHeld = shouldHoldUpdatesForFormFill();
  wasFillOpen = isFormFillInProgress();
  unsubscribeFormFillGuard = subscribeFormFill(onFormFillChange);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', onForegroundTrigger);
    window.addEventListener('online', onForegroundTrigger);
  }
}

// Module-level singleton wiring, mirroring applyUpdate.ts's own pattern. Safe
// to subscribe unconditionally at import time: formFillGuard and document are
// always present in a browser bundle. Each real module instance (one per
// bundle / test's `jest.isolateModules`) wires exactly once.
wireModuleLevelListeners();

/**
 * Wire the watcher to a live registration. Call once, from
 * `useRegisterSW`'s `onRegisteredSW` (client-tech, time-client) or
 * `navigator.serviceWorker.ready` (sales-client, which doesn't use the React
 * hook). Safe to call again with a new registration (e.g. hot reload in dev)
 * — the previous registration's listener is left in place but inert once
 * `registration` is reassigned, since all reads go through the shared
 * `registration` variable rather than a closure over the old one.
 */
export function registerSwUpdateTarget(
  reg: ServiceWorkerRegistration,
  name: string,
): void {
  // A remount (a `useRef` resetting) can call this again with the IDENTICAL
  // registration object -- skip re-adding the listener in that case so a
  // single native `updatefound` isn't wired to fire our handler more than
  // once for the same registration (see file header: "Call once").
  const isSameRegistration = registration === reg;
  registration = reg;
  appName = name;
  if (!isSameRegistration) {
    registration.addEventListener('updatefound', onUpdateFound);
  }
  if (!attached && typeof navigator !== 'undefined' && navigator.serviceWorker) {
    attached = true;
    hadControllerAtAttach = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
  }
}

/**
 * Ask the browser to look for a new service worker. Throttled to at most
 * once per THROTTLE_MS UNLESS `force` is set (an explicit user action, or the
 * guard-release check above) — and the throttle is stamped only when the
 * check RESOLVES, never when it rejects or while one is already in flight.
 */
export function checkForSwUpdate(force = false): void {
  if (!registration || checkInFlight) return;
  const now = Date.now();
  if (!force && now - lastSuccessfulCheckAt < THROTTLE_MS) return;
  checkInFlight = true;
  registration
    .update()
    .then(() => {
      lastSuccessfulCheckAt = Date.now();
    })
    .catch(() => undefined)
    .finally(() => {
      checkInFlight = false;
    });
}

/**
 * Shared handler for any "the page just got taken over" signal that isn't
 * itself `controllerchange` — e.g. vite-plugin-pwa's `onNeedReload`, called
 * from register.js on workbox's `activated` event. Routes through the same
 * defer-while-guarded logic as `onControllerChange` so the two signals (which
 * fire around the same real-world moment) agree, and `reloadAfterFlush`'s own
 * singleton guard makes calling both harmless.
 */
export function handleSwTakeoverSignal(): void {
  // Deliberately calls `handleTakeover` directly, NOT `onControllerChange`:
  // this is register.js's `onNeedReload`, called only when workbox's own
  // `isUpdate || isExternal` check already fired -- i.e. vite-plugin-pwa has
  // ALREADY filtered out the first-install case upstream, so the
  // `hadControllerAtAttach` guard (which exists for the native
  // `controllerchange` listener, which sees every activation including the
  // first) does not apply here.
  handleTakeover();
}

/**
 * Test-only: reset all module-level singleton state AND detach the two
 * listeners wired at import time, then re-wire them fresh. Needed because
 * `document` (unlike `navigator.serviceWorker`, which tests replace with a
 * fresh fake per test) is REAL and shared across every test in a file — a
 * module-level `document.addEventListener` from a PRIOR `jest.isolateModules`
 * instance would otherwise keep firing (against its own, now-stale
 * `registration` closure) for every test that follows it.
 */
export function resetSwUpdateWatcherForTests(): void {
  if (registration) registration.removeEventListener('updatefound', onUpdateFound);
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('focus', onForegroundTrigger);
    window.removeEventListener('online', onForegroundTrigger);
  }
  if (unsubscribeFormFillGuard) unsubscribeFormFillGuard();
  registration = undefined;
  appName = 'unknown';
  lastSuccessfulCheckAt = 0;
  checkInFlight = false;
  reloadDeferred = false;
  updateFoundCount = 0;
  wasFillHeld = false;
  wasFillOpen = false;
  attached = false;
  hadControllerAtAttach = false;
  wireModuleLevelListeners();
}

/** Test/telemetry hook: how many native `updatefound` events our OWN
 * registration-level listener has observed, independent of workbox-window's
 * internal (buggy) bookkeeping. */
export function getSwUpdateFoundCount(): number {
  return updateFoundCount;
}
